// import { useLanguage } from "@/api/AppApi/config/use-language";
import { apiConfig } from "../../config";
import ApiCart, { APIGetUserLocation } from "./api-cart";
import {
  IGetCart,
  IPostPaymentMethodAvailableResult,
  ISubmitCheckoutResponse,
} from "./interfaces/cart-interfaces";
import { useGetCompanyQuery } from "../company";
import { getKey } from "../../common-interfaces/quiries-keys/queries-keys";
import { Keys } from "../../common-interfaces/quiries-keys/keysValues";
import {
  useQuery,
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { usePathname, useSearchParams } from "next/navigation";
import useSellingMode from "@/hooks/useSellingMode";

export const useCartApi = () => new ApiCart(apiConfig());

export const useGetCartQuery = () => {
  const getCompany = useGetCompanyQuery();
  const { show_cart } = getCompany?.data?.result?.theme?.settings ?? {};

  const { enableSells, isFetching } = useSellingMode();

  const cartApi = useCartApi();
  return useQuery([...getKey(Keys.get_cart)], cartApi.getCart, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!(show_cart && !!enableSells && !isFetching),
  });
};

export const updateGetCartQueryData = (
  queryClient: QueryClient,
  data: IGetCart,
) => {
  queryClient.setQueryData([...getKey(Keys.get_cart, false, "en")], data);
  queryClient.setQueryData([...getKey(Keys.get_cart, false, "ar")], data);
};
export const useGetShippingMethods = (areaId: string) => {
  const cartApi = useCartApi();
  return useQuery(
    [...(getKey(Keys.get_shipping_mthods) + areaId)],
    () => cartApi.getShippingMethods(areaId),
    {
      refetchOnWindowFocus: false,
      enabled: +areaId > 0,
    },
  );
};
export const useGetPickupMethods = () => {
  const cartApi = useCartApi();
  return useQuery([Keys.get_pickup_mthods], () => cartApi.getpickupMethods(), {
    refetchOnWindowFocus: false,
  });
};

export const useSetShippingMethod = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.setShippingMethod, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};
export const useSetPaymentMethod = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.setPaymentMethod, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};
export const useManageCartItem = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  const couponAPI = usePostCoupon({
    onSuccess: () => {
      localStorage.removeItem("coupon_referer");
    },
    onError: () => {},
  });
  return useMutation(cart.manageCartItem, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res as IGetCart);
      const coupon = localStorage.getItem("coupon_referer");
      if (coupon) {
        couponAPI.mutate({ code: coupon, country_code: "" });
      }
      onSuccess(res);
    },
    onError: onError,
    mutationKey: ["post-item"],
  });
};

export const useRemoveCartItem = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const cart_item_referer = searchParams.get("cart_item_referer");
  return useMutation(cart.removeCartItem, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res);
      onSuccess(res);
      if (!!cart_item_referer) router.push(pathName);
    },
    onError: onError,
  });
};

export const useClearCart = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();

  return useMutation(cart.clearCart, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res as IGetCart);
      onSuccess(res);
    },
    onError: onError,
  });
};

export const usePostCoupon = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.postCoupons, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};

export const useDeleteCoupon = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.deleteCoupons, {
    onSuccess: (res) => {
      onSuccess(res);
      updateGetCartQueryData(queryClient, res as IGetCart);
    },
    onError: onError,
  });
};

export const usePostRefreshCart = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: any) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  const queryClient = useQueryClient();
  return useMutation(cart.refresh, {
    onSuccess: (res) => {
      updateGetCartQueryData(queryClient, res as IGetCart);
      onSuccess(res);
    },
    onError: onError,
  });
};

export const useSubmitCheckout = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: ISubmitCheckoutResponse) => void;
  onError: (err: any) => void;
}) => {
  const getCart = useGetCartQuery();
  const cart = useCartApi();
  return useMutation(cart.submitCheckout, {
    onSuccess: (res) => {
      onSuccess(res);
      getCart.refetch();
    },
    onError: onError,
  });
};

export const useGetPaymentQuery = ({
  track_id,
  enabled,
}: {
  track_id: string;
  enabled: boolean;
}) => {
  const cartAPI = useCartApi();
  return useQuery(
    [...getKey(Keys.get_payment), track_id],
    () => cartAPI.getPayments(track_id),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useGetPaymentMethods = ({
  country_code,
  enabled = true,
}: {
  country_code: string;
  enabled?: boolean;
}) => {
  const cartApi = useCartApi();
  return useQuery(
    [...(getKey(Keys.get_payment_mthods) + country_code)],
    () => cartApi.getPaymentMethods(country_code),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useGetUserLocation = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery([...getKey(Keys.user_locaion)], () => APIGetUserLocation(), {
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

export const useGetDeliveryBranches = ({
  address_id,
  area_id,
  enabled,
}: {
  address_id?: number;
  area_id?: number;
  enabled?: boolean;
}) => {
  const cartApi = useCartApi();
  return useQuery(
    [
      ...(getKey(Keys.get_Delivery_branches) +
        (address_id ?? area_id)?.toString()!),
    ],
    () => cartApi.getDeliveryBranches(address_id!, area_id!),
    {
      refetchOnWindowFocus: false,
      enabled: enabled,
    },
  );
};

export const useSubmitPaymentMethodAvailable = ({
  onSuccess,
  onError,
}: {
  onSuccess: (res: IPostPaymentMethodAvailableResult) => void;
  onError: (err: any) => void;
}) => {
  const cart = useCartApi();
  return useMutation(cart.postPaymentMethodAvailable, {
    onSuccess: (res) => {
      onSuccess(res);
    },
    onError: onError,
  });
};
